import { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAdmin } from "@/context/AdminAuth";
import { http, errMsg } from "@/lib/api";

export function AuthCallback() {
  const { hash } = useLocation();
  const navigate = useNavigate();
  const { setUser } = useAdmin();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(hash.slice(1)).get("session_id");
    http.post("/auth/google/session", {}, { headers: { "X-Session-ID": sid } })
      .then((r) => { setUser(r.data); navigate("/admin/dashboard", { replace: true }); })
      .catch((e) => navigate("/admin", { replace: true, state: { error: errMsg(e) } }));
  }, [hash, navigate, setUser]);
  return <div className="flex min-h-screen items-center justify-center bg-navy"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>;
}

export function AdminLogin() {
  const { user, setUser } = useAdmin();
  const loc = useLocation();
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState(loc.state?.error || "");
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to="/admin/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await http.post("/auth/login", f);
      setUser(data);
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="grain relative flex min-h-screen items-center justify-center overflow-hidden bg-navy px-5" data-testid="admin-login-page">
      <img src="/img/london-aerial.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-15" />
      <div className="relative z-10 w-full max-w-md border border-gold/30 bg-ivory p-8 sm:p-12">
        <img src="/brand/logo.webp" alt="Immistop crest" className="mx-auto h-20 w-auto" />
        <h1 className="mt-6 text-center font-serif text-4xl text-navy">Admin access</h1>
        <p className="mt-2 text-center text-[13px] text-charcoal-soft">Authorised personnel only.</p>
        <form onSubmit={submit} className="mt-10 space-y-7">
          <label className="block"><span className="label-lux">Email</span><input type="email" required className="input-lux" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="admin-email-input" /></label>
          <label className="block"><span className="label-lux">Password</span><input type="password" required className="input-lux" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="admin-password-input" /></label>
          {err && <p className="text-[13px] text-destructive" role="alert" data-testid="admin-login-error">{err}</p>}
          <button type="submit" disabled={busy} className="btn-navy w-full" data-testid="admin-login-submit-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}</button>
        </form>
        <div className="my-6 flex items-center gap-4 text-[11px] uppercase tracking-[0.2em] text-charcoal-soft"><span className="h-px flex-1 bg-navy/15" />or<span className="h-px flex-1 bg-navy/15" /></div>
        <button type="button" onClick={google} className="btn-outline-dark w-full" data-testid="admin-google-login-btn">Continue with Google</button>
        <p className="mt-4 text-center text-[12px] text-charcoal-soft">Google sign-in is limited to allow-listed accounts.</p>
      </div>
    </div>
  );
}
